import { useEffect, useState } from "react";
import { LogOut, Plus, Trash2, X, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const LEAD_STATUSES = ["new", "contacted", "qualified", "closed"];
const PLATFORM_STATUSES = ["draft", "coming_soon", "live", "retired"];

function PlatformModal({ platform, onClose, onSaved }) {
  const isEdit = Boolean(platform?.id);
  const [form, setForm] = useState({
    slug: platform?.slug || "",
    name: platform?.name || "",
    tagline: platform?.tagline || "",
    primary_url: platform?.primary_url || "",
    hero_image_url: platform?.hero_image_url || "",
    ownership: platform?.ownership || "internal",
    status: platform?.status || "live",
    launched_at: platform?.launched_at || "",
    categories: (platform?.categories || []).join(", "),
    summary: platform?.summary || "",
    case_study_md: platform?.case_study_md || "",
  });
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    setError("");
    const payload = {
      ...form,
      launched_at: form.launched_at || null,
      categories: form.categories.split(",").map((c) => c.trim()).filter(Boolean),
    };
    try {
      if (isEdit) {
        const { slug, ...body } = payload; // slug is immutable on edit
        await api.patch(`/admin/platforms/${platform.id}`, body);
      } else {
        await api.post("/admin/platforms", payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save");
    }
  };
  const input = "w-full bg-black border border-zinc-800 px-3 py-2 text-white text-sm focus:border-tib-gold focus:outline-none";

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6" data-testid="platform-modal">
      <div className="card-edge w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-5">
          <h3 className="h-serif text-xl text-white">{isEdit ? `Edit ${platform.name}` : "New platform"}</h3>
          <button onClick={onClose} data-testid="modal-close"><X className="w-5 h-5 text-zinc-500" /></button>
        </div>
        <div className="space-y-3">
          <input placeholder="slug (unique)" data-testid="pf-slug" disabled={isEdit} className={`${input} ${isEdit ? "opacity-50 cursor-not-allowed" : ""}`} value={form.slug} onChange={set("slug")} />
          <input placeholder="Name" data-testid="pf-name" className={input} value={form.name} onChange={set("name")} />
          <input placeholder="Tagline" className={input} value={form.tagline} onChange={set("tagline")} />
          <input placeholder="Primary URL (e.g. https://example.com)" className={input} value={form.primary_url} onChange={set("primary_url")} />
          <input placeholder="Hero image URL" className={input} value={form.hero_image_url} onChange={set("hero_image_url")} />
          <input placeholder="Launched at (e.g. 2026-06-01)" className={input} value={form.launched_at} onChange={set("launched_at")} />
          <input placeholder="Categories (comma separated)" className={input} value={form.categories} onChange={set("categories")} />
          <div className="grid grid-cols-2 gap-3">
            <select className={input} value={form.ownership} onChange={set("ownership")}>
              <option value="internal">internal</option>
              <option value="external">external</option>
            </select>
            <select className={input} value={form.status} onChange={set("status")}>
              {PLATFORM_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <textarea placeholder="Summary" rows={2} className={input} value={form.summary} onChange={set("summary")} />
          <textarea placeholder="Case study (markdown)" rows={5} className={input} value={form.case_study_md} onChange={set("case_study_md")} />
          {error && <p className="text-red-400 text-sm">{String(error)}</p>}
          <button onClick={save} data-testid="pf-save" className="btn-primary w-full justify-center">{isEdit ? "Save changes" : "Create platform"}</button>
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("platforms");
  const [platforms, setPlatforms] = useState([]);
  const [leads, setLeads] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const loadPlatforms = () => api.get("/admin/platforms").then((r) => setPlatforms(r.data)).catch(() => {});
  const loadLeads = () => api.get("/admin/leads").then((r) => setLeads(r.data)).catch(() => {});

  useEffect(() => { loadPlatforms(); loadLeads(); }, []);

  const deletePlatform = async (id) => {
    if (!window.confirm("Delete this platform?")) return;
    await api.delete(`/admin/platforms/${id}`);
    loadPlatforms();
  };

  const setLeadStatus = async (id, status) => {
    await api.patch(`/admin/leads/${id}`, { status });
    loadLeads();
  };

  const setPlatformStatus = async (id, status) => {
    await api.patch(`/admin/platforms/${id}`, { status });
    loadPlatforms();
  };

  return (
    <div className="min-h-screen bg-black" data-testid="admin-dashboard">
      <header className="border-b border-zinc-900 sticky top-0 bg-black/90 backdrop-blur z-40">
        <div className="container-tib h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/tib-logo.png" alt="TIB" className="w-7 h-7 object-contain" />
            <span className="font-mono text-sm tracking-widest uppercase text-white">TIB Console</span>
          </div>
          <div className="flex items-center gap-5">
            <span className="text-xs text-zinc-500 hidden sm:block">{user?.email}</span>
            <button onClick={logout} data-testid="admin-logout" className="text-zinc-400 hover:text-tib-amber flex items-center gap-2 text-sm">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="container-tib py-10">
        <div className="flex gap-2 mb-8">
          {["platforms", "leads"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`tab-${t}`}
              className={`px-5 py-2 text-sm font-mono uppercase tracking-widest border transition-colors ${tab === t ? "border-tib-amber text-tib-amber" : "border-zinc-800 text-zinc-500 hover:text-white"}`}
            >
              {t} {t === "platforms" ? `(${platforms.length})` : `(${leads.length})`}
            </button>
          ))}
        </div>

        {tab === "platforms" && (
          <div data-testid="admin-platforms">
            <div className="flex justify-end mb-5">
              <button onClick={() => { setEditing(null); setModalOpen(true); }} data-testid="add-platform" className="btn-primary text-sm py-2 px-4">
                <Plus className="w-4 h-4" /> Add platform
              </button>
            </div>
            <div className="space-y-3">
              {platforms.map((p) => (
                <div key={p.id} className="card-edge p-5 flex items-center justify-between gap-4" data-testid={`admin-platform-${p.slug}`}>
                  <div className="min-w-0">
                    <div className="text-white font-semibold">{p.name} <span className="text-zinc-600 font-mono text-xs">/{p.slug}</span></div>
                    <div className="text-zinc-500 text-sm truncate">{p.tagline}</div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <select value={p.status} onChange={(e) => setPlatformStatus(p.id, e.target.value)} className="bg-black border border-zinc-800 text-xs text-white px-2 py-1">
                      {PLATFORM_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button onClick={() => { setEditing(p); setModalOpen(true); }} data-testid={`edit-${p.slug}`} className="text-zinc-600 hover:text-tib-gold"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => deletePlatform(p.id)} data-testid={`delete-${p.slug}`} className="text-zinc-600 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "leads" && (
          <div className="space-y-3" data-testid="admin-leads">
            {leads.length === 0 && <p className="text-zinc-500">No leads yet.</p>}
            {leads.map((l) => (
              <div key={l.id} className="card-edge p-5" data-testid={`admin-lead-${l.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div>
                    <div className="text-white font-semibold">{l.name} <span className="text-zinc-500 font-normal text-sm">· {l.email}</span></div>
                    <div className="flex gap-2 mt-1">
                      <span className="text-[0.6rem] font-mono uppercase tracking-widest text-tib-amber border border-tib-amber/40 px-2 py-0.5">{l.inquiry_type}</span>
                      {l.company && <span className="text-xs text-zinc-500">{l.company}</span>}
                    </div>
                  </div>
                  <select value={l.status} onChange={(e) => setLeadStatus(l.id, e.target.value)} data-testid={`lead-status-${l.id}`} className="bg-black border border-zinc-800 text-xs text-white px-2 py-1">
                    {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                {l.message && <p className="text-zinc-400 text-sm mt-3 leading-relaxed">{l.message}</p>}
                <div className="text-[0.65rem] font-mono text-zinc-700 mt-3">{l.created_at}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {modalOpen && <PlatformModal platform={editing} onClose={() => setModalOpen(false)} onSaved={loadPlatforms} />}
    </div>
  );
}
